import React, { useEffect, useState } from 'react';
import { Instagram as InstagramIcon, Play, ExternalLink, Images } from 'lucide-react';
import axios from 'axios';
import { trackInstagram } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaBadge = ({ type }) => {
  if (type === 'VIDEO') {
    return (
      <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-full p-1.5 shadow-lg">
        <Play className="h-3.5 w-3.5 text-white fill-white" />
      </div>
    );
  }
  if (type === 'CAROUSEL_ALBUM') {
    return (
      <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm rounded-full p-1.5 shadow-lg">
        <Images className="h-3.5 w-3.5 text-white" />
      </div>
    );
  }
  return null;
};

const getThumb = (item) => {
  // Videos: prefer thumbnail_url when available (media_url is the video file)
  if (item.media_type === 'VIDEO') return item.thumbnail_url || item.media_url;
  return item.media_url;
};

const InstagramSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="instagram-skeleton">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={`sk-${i}`} className="aspect-square bg-zinc-900/60 rounded-sm animate-pulse"></div>
    ))}
  </div>
);

const InstagramFeed = () => {
  const [items, setItems] = useState([]);
  const [username, setUsername] = useState('abrbarbearia');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    axios
      .get(`${API_URL}/api/instagram/media?limit=12`, { signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        setItems(res.data.items || []);
        if (res.data.username) setUsername(res.data.username);
      })
      .catch((err) => {
        if (cancelled || axios.isCancel(err)) return;
        setError('Não foi possível carregar o feed do Instagram no momento.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return (
    <section id="instagram" className="py-20 bg-black/60 backdrop-blur-md" data-testid="instagram-section">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 mb-6">
            <InstagramIcon className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs uppercase tracking-widest font-semibold">Instagram · Ao vivo</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Últimos <span className="text-[#D4AF37]">trabalhos</span>
          </h2>
          <p className="text-gray-400 text-lg">
            Direto do nosso Instagram — atualizado em tempo real.
          </p>
          <a
            href={`https://instagram.com/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-profile-link"
            onClick={() => trackInstagram('profile_link')}
            className="inline-flex items-center gap-2 mt-4 text-[#D4AF37] hover:text-white transition-colors font-medium"
          >
            @{username}
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>

        {loading && <InstagramSkeleton />}

        {!loading && error && (
          <div className="text-center py-12" data-testid="instagram-error">
            <p className="text-gray-400 mb-4">{error}</p>
            <a
              href={`https://instagram.com/${username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 rounded-sm"
            >
              <InstagramIcon className="h-5 w-5" />
              Ver no Instagram
            </a>
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="instagram-grid">
              {items.map((item) => (
                <a
                  key={item.id}
                  href={item.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`instagram-item-${item.id}`}
                  onClick={() => trackInstagram('post_click', { post_id: item.id, media_type: item.media_type })}
                  className="relative aspect-square overflow-hidden rounded-sm group cursor-pointer block bg-zinc-900"
                >
                  <img
                    src={getThumb(item)}
                    alt={item.caption ? item.caption.slice(0, 80) : 'Instagram post'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <MediaBadge type={item.media_type} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-between p-4">
                    <InstagramIcon className="h-6 w-6 text-white drop-shadow-lg" />
                    <ExternalLink className="h-5 w-5 text-white drop-shadow-lg" />
                  </div>
                </a>
              ))}
            </div>

            <div className="text-center mt-12">
              <a
                href={`https://instagram.com/${username}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-follow-btn"
                onClick={() => trackInstagram('follow_button')}
                className="inline-flex items-center gap-2 px-8 py-3 bg-[#D4AF37] hover:bg-[#B8941F] text-black font-semibold rounded-sm transition-all duration-300 hover:scale-105"
              >
                <InstagramIcon className="h-5 w-5" />
                Seguir no Instagram
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default InstagramFeed;
